/**
 * Wave E (decision 0063, spec §7.4): runner presence pushed live. One `/api/workspaces/:id/live` socket per workspace
 * (Effect RPC, `WorkspaceLiveRpcs.WatchRunners`), shared by every mounted pill and reference-counted like the project
 * socket. Each `RunnerLiveEvent` is merged into the `runnersQuery` cache (the v1 items are derived from it), so the
 * pill, the runners page and the Team tab change within a second, without the old 5 s poll. The push carries only the
 * public view (no paths, accounts or leases): the owner's extra fields come from `GET …/runners`, refetched shortly
 * after a change.
 */
import type { RunnerLiveEvent, RunnerPublicView } from "@minga/protocol"
import { WorkspaceLiveRpcs } from "@minga/protocol"
import { makeDurableRpcClient } from "@minga/protocol/durable-rpc-client"
import { type QueryClient, useQueryClient } from "@tanstack/react-query"
import { Effect, Fiber, Layer, type Schedule, Stream } from "effect"
import { Socket } from "effect/socket"
import { useEffect } from "react"
import { jitteredBackoff } from "@/model/live/backoff"
import { type RunnerRow, runnersQuery } from "./runner-api"
import { mergeLive } from "./runner-groups"

/** The `/live` URL of a workspace, from the page's origin (http → ws, https → wss). */
export const runnersLiveUrl = (origin: string, workspaceId: string): string =>
  `${origin.replace(/^http/, "ws")}/api/workspaces/${encodeURIComponent(workspaceId)}/live`

/** A runner first seen on the push: the public fields only, until `GET …/runners` brings the rest. */
export const rowOfView = (v: RunnerPublicView): RunnerRow => ({
  runnerId: v.runnerId,
  name: v.name,
  os: v.os,
  arch: v.arch,
  version: v.version,
  ownerUserId: v.ownerUserId,
  status: v.status,
  lastSeenAt: v.lastSeenAt,
  serves: v.serves,
  ...(v.activeRuns !== undefined ? { activeRuns: v.activeRuns } : {}),
})

/** How long after a push the full list is fetched again (the owner's leases, profiles and logins). */
export const REFETCH_AFTER_MS = 1_500

/**
 * Fold one event into the caches. Returns whether the full list should be refetched (a runner changed, so the owner's
 * extra fields may have too).
 *
 * Performance follow-up (0151 proposal 9): every subscription starts with a `RunnerUpdated` per known runner, which
 * used to refetch `GET …/runners` 1.5 s after every page load (the header asked twice). An event that changes nothing
 * the list holds (apart from `lastSeenAt`, a heartbeat) does not refetch, nor does one that arrives before the list
 * has loaded (its own GET brings everything).
 */
export const applyRunnerEvent = (qc: QueryClient, workspaceId: string, event: RunnerLiveEvent): boolean => {
  const key = runnersQuery(workspaceId).queryKey
  const before = qc.getQueryData(key)
  if (!before) return false
  const after = mergeLive(before, event, rowOfView) as RunnerRow[]
  if (sameRunners(before, after)) {
    // a heartbeat: keep "last seen" current without asking the server again
    if (after.some((r, i) => r.lastSeenAt !== before[i]?.lastSeenAt)) qc.setQueryData(key, after)
    return false
  }
  qc.setQueryData(key, after)
  return true
}

/** The fields a push can change, `lastSeenAt` aside, in a stable order. */
const pushed = (r: RunnerRow): string =>
  stableJson([
    r.runnerId,
    r.name,
    r.os,
    r.arch,
    r.ownerUserId,
    r.status,
    r.serves,
    r.version,
    r.activeRuns ?? null,
  ])

const sameRunners = (a: ReadonlyArray<RunnerRow>, b: ReadonlyArray<RunnerRow>): boolean =>
  a.length === b.length &&
  a.every((r, i) => {
    const s = b[i]
    return s !== undefined && pushed(r) === pushed(s)
  })

const stableJson = (v: unknown): string =>
  JSON.stringify(v, (_, x: unknown) =>
    x && typeof x === "object" && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x).sort(([p], [q]) => (p < q ? -1 : p > q ? 1 : 0)))
      : x,
  )

interface Entry {
  refs: number
  closeTimer: ReturnType<typeof setTimeout> | null
  refetchTimer: ReturnType<typeof setTimeout> | null
  fiber: Fiber.Fiber<unknown, unknown> | null
}

const CLOSE_GRACE_MS = 5_000
const entries = new Map<string, Entry>()

// 0.5 s doubling up to 15 s, with jitter: after a deploy the tabs don't all reconnect at once.
const retry: Schedule.Schedule<unknown, unknown> = jitteredBackoff("500 millis", 15_000)

const open = (qc: QueryClient, workspaceId: string, url: string): Entry => {
  const entry: Entry = { refs: 0, closeTimer: null, refetchTimer: null, fiber: null }
  const onEvent = (event: RunnerLiveEvent) => {
    if (!applyRunnerEvent(qc, workspaceId, event)) return
    if (entry.refetchTimer) clearTimeout(entry.refetchTimer)
    entry.refetchTimer = setTimeout(() => {
      entry.refetchTimer = null
      void qc.invalidateQueries({ queryKey: runnersQuery(workspaceId).queryKey, exact: true })
    }, REFETCH_AFTER_MS)
  }
  const socket = Socket.layerWebSocket(url).pipe(Layer.provide(Socket.layerWebSocketConstructorGlobal))
  entry.fiber = Effect.runFork(
    Effect.scoped(
      Effect.gen(function* () {
        const client = yield* makeDurableRpcClient(WorkspaceLiveRpcs, socket)
        // Every (re)subscription starts with a RunnerUpdated per known runner, so a reconnect catches up by itself.
        return yield* client.WatchRunners().pipe(
          Stream.runForEach((event) => Effect.sync(() => onEvent(event))),
          Effect.andThen(Effect.sleep("250 millis")),
          Effect.retry(retry),
          Effect.forever,
        )
      }),
    ).pipe(Effect.catchDefect((defect) => Effect.logError("runner live defect", defect))),
  )
  return entry
}

const close = (key: string, entry: Entry) => {
  if (entries.get(key) !== entry) return
  entries.delete(key)
  if (entry.closeTimer) clearTimeout(entry.closeTimer)
  if (entry.refetchTimer) clearTimeout(entry.refetchTimer)
  if (entry.fiber) Effect.runFork(Fiber.interrupt(entry.fiber))
}

/** Take a reference to the workspace's runner push, opening it if needed. Call the returned function to release. */
export const acquireRunnersLive = (qc: QueryClient, workspaceId: string): (() => void) => {
  const origin = globalThis.location?.origin ?? "http://localhost"
  let entry = entries.get(workspaceId)
  if (!entry) {
    entry = open(qc, workspaceId, runnersLiveUrl(origin, workspaceId))
    entries.set(workspaceId, entry)
  }
  const e = entry
  e.refs += 1
  if (e.closeTimer) {
    clearTimeout(e.closeTimer)
    e.closeTimer = null
  }
  let released = false
  return () => {
    if (released) return
    released = true
    e.refs -= 1
    if (e.refs > 0) return
    e.closeTimer = setTimeout(() => close(workspaceId, e), CLOSE_GRACE_MS)
  }
}

/** Keep the workspace's runner push open while the component is mounted (the pill mounts it on every page). */
export function useRunnersLive(workspaceId: string | null): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!workspaceId || typeof WebSocket === "undefined") return
    return acquireRunnersLive(qc, workspaceId)
  }, [qc, workspaceId])
}
